import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast - Temporary notification with optional action
 *
 * Features:
 * - Auto-dismiss after duration
 * - Optional action button (e.g., Undo)
 * - Smooth enter/exit animations
 * - Multiple variants (success, error, info)
 */
function Toast({
  isVisible,
  message,
  actionLabel,
  onAction,
  onClose,
  duration = 5000,
  variant = 'default' // 'default' | 'success' | 'error'
}) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle auto-dismiss
  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);

      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      // Delay removing from DOM for exit animation
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible && !isAnimating) return null;

  const variantClasses = {
    default: 'bg-gray-800 dark:bg-gray-700',
    success: 'bg-green-600 dark:bg-green-700',
    error: 'bg-red-600 dark:bg-red-700'
  };

  // Portal to <body>, so a transformed ancestor (the sidebar) can't move it.
  return createPortal(
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50
        flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg
        text-white text-sm gpu-accelerated
        ${variantClasses[variant]}
        ${isVisible
          ? 'animate-slideInUp'
          : 'animate-slideOutDown'
        }`}
    >
      {/* Icon based on variant */}
      {variant === 'success' && (
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
      {variant === 'error' && (
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}

      {/* Message */}
      <span className="flex-1">{message}</span>

      {/* Action button */}
      {actionLabel && onAction && (
        <button
          onClick={() => {
            onAction();
            onClose();
          }}
          className="px-2 py-1 text-sm font-medium
            bg-white/20 hover:bg-white/30
            rounded transition-colors"
        >
          {actionLabel}
        </button>
      )}

      {/* Close button */}
      <button
        onClick={onClose}
        className="p-1 hover:bg-white/20 rounded-sm transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>,
    document.body,
  );
}

export default Toast;
